import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../services/api';
import { useAuth } from '../store/AuthContext';
import {
  Download,
  CheckCircle,
  AlertCircle,
  Loader2,
  Map,
  Search,
  RefreshCw,
  Database,
  Globe,
} from 'lucide-react';

// Fuentes históricas con ruta propia en el backend. Las marcas de intelSources
// (Goiko, Papa John's, VIPS, Foster's, Five Guys, Pizza Hut, Pans, Carl's Jr.,
// Rodilla, VICIO, TGB, Taco Bell, Domino's, Wingstop, Jollibee) se cargan en
// tiempo de ejecución desde GET /intel/brands y se añaden a esta lista.
const BASE_SOURCES = [
  {
    key: 'mcdonalds',
    label: "McDonald's",
    description: "Restaurantes McDonald's España — API oficial con fallback a archivo local",
    color: '#009639',
    endpoint: '/intel/fetch/mcdonalds',
    sourceId: 'mcdonalds',
  },
  {
    key: 'bk',
    label: 'BK',
    description: 'Restaurantes BK España — API oficial RBI GraphQL',
    color: '#E4002B',
    endpoint: '/intel/fetch/bk',
    sourceId: 'bk',
  },
  {
    key: 'kfc',
    label: 'KFC',
    description: 'Restaurantes KFC España — API oficial',
    color: '#FFFFFF',
    endpoint: '/intel/fetch/kfc',
    sourceId: 'kfc',
  },
  {
    key: 'popeye',
    label: 'Popeyes',
    description: 'Restaurantes Popeyes (PLK) España — API oficial RBI GraphQL (euw3-prod-plk.rbictg.com)',
    color: '#FF6600',
    endpoint: '/intel/fetch/popeye',
    sourceId: 'popeye',
  },
  {
    key: 'carrefour',
    label: 'Carrefour',
    description: 'Tiendas Carrefour España — OpenStreetMap / Overpass API (datos geoespaciales en tiempo real)',
    color: '#003DA5',
    endpoint: '/intel/fetch/carrefour',
    sourceId: 'carrefour',
  },
];

export default function IntelPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const API = getApiUrl();

  const [stats, setStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [fetchState, setFetchState] = useState({}); // { [key]: 'idle'|'loading'|'success'|'error', message }

  // Marcas descubiertas dinámicamente (intelSources) + fuentes históricas.
  const [sources, setSources] = useState(BASE_SOURCES);

  // Overpass form state
  const [osmBrand, setOsmBrand] = useState('');
  const [osmCountry, setOsmCountry] = useState('Spain');
  const [osmType, setOsmType] = useState('competence');
  const [osmLoading, setOsmLoading] = useState(false);
  const [osmResult, setOsmResult] = useState(null);

  // Type selector per direct source
  const [sourceTypes, setSourceTypes] = useState(
    Object.fromEntries(BASE_SOURCES.map((s) => [s.key, 'competence']))
  );

  const headers = { Authorization: `Bearer ${token}` };

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await axios.get(`${API}/intel/sources`, { headers });
      setStats(data.sources || []);
    } catch {
      // noop — individual card errors are not critical
    } finally {
      setLoadingStats(false);
    }
  }, [API, token]);

  const loadBrands = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/intel/brands`, { headers });
      const brandSources = (data.brands || []).map((b) => ({
        key: b.key,
        label: b.label,
        description: b.description,
        color: b.color,
        origin: b.origin, // 'official' | 'osm'
        endpoint: `/intel/fetch/${b.key}`,
        sourceId: b.key,
      }));
      const merged = [...BASE_SOURCES, ...brandSources];
      setSources(merged);
      setSourceTypes((prev) => {
        const next = { ...prev };
        for (const s of merged) if (!(s.key in next)) next[s.key] = 'competence';
        return next;
      });
    } catch {
      // Si el catálogo falla, seguimos con las fuentes históricas.
    }
  }, [API, token]);

  useEffect(() => {
    loadStats();
    loadBrands();
  }, [loadStats, loadBrands]);

  const [cleanupState, setCleanupState] = useState(null); // null | 'loading' | mensaje

  const handleCleanupCoords = async () => {
    setCleanupState('loading');
    try {
      const { data } = await axios.post(`${API}/intel/cleanup-coords`, {}, { headers });
      setCleanupState(data.message || 'Limpieza completada');
      loadStats();
    } catch (err) {
      setCleanupState(err.response?.data?.message || 'Error al limpiar puntos');
    }
  };

  const getStatForSource = (sourceId) =>
    stats.find((s) => s.source === sourceId) || { count: 0, status: 'empty', lastIngested: null };

  const handleFetch = async (src) => {
    setFetchState((prev) => ({ ...prev, [src.key]: { status: 'loading' } }));
    try {
      const { data } = await axios.post(`${API}${src.endpoint}`, { type: sourceTypes[src.key] || 'competence' }, { headers });
      setFetchState((prev) => ({
        ...prev,
        [src.key]: { status: 'success', message: data.message },
      }));
      loadStats();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setFetchState((prev) => ({ ...prev, [src.key]: { status: 'error', message: msg } }));
    }
  };

  const handleOsmImport = async (e) => {
    e.preventDefault();
    if (!osmBrand.trim()) return;
    setOsmLoading(true);
    setOsmResult(null);
    try {
      const { data } = await axios.post(
        `${API}/turbo/import`,
        { brandName: osmBrand.trim(), country: osmCountry, type: osmType, targetCollection: osmType === 'competence' ? 'competition' : 'base' },
        { headers }
      );
      setOsmResult({ status: 'success', message: data.message || `${data.count} locales importados` });
      loadStats();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setOsmResult({ status: 'error', message: msg });
    } finally {
      setOsmLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#05070A',
        color: 'white',
        padding: 'clamp(16px, 4vw, 30px)',
        fontFamily: "'Orbitron', sans-serif",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Database size={28} color="#00CEC9" />
          <h1 style={{ margin: 0, fontSize: '22px', letterSpacing: '3px', color: '#00CEC9' }}>
            INTEL SOURCES
          </h1>
          <button
            onClick={loadStats}
            title="Refresh stats"
            style={{
              background: 'none',
              border: 'none',
              color: '#636e72',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '8px',
            }}
          >
            <RefreshCw size={16} style={{ animation: loadingStats ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
        <p style={{ margin: 0, fontSize: '11px', color: '#636e72', letterSpacing: '1px' }}>
          DESCARGA Y GESTIONA LOS DATOS DE LOCALES EN EL MAPA
        </p>

        {/* Mantenimiento: eliminar puntos con coordenadas inválidas (aparecían
            en el mar/desierto por datos de origen ausentes). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px', flexWrap: 'wrap' }}>
          <button
            onClick={handleCleanupCoords}
            disabled={cleanupState === 'loading'}
            title="Elimina locales con coordenadas 0,0 o fuera de rango"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 14px',
              backgroundColor: 'transparent',
              color: '#E27B58',
              border: '1px solid #E27B58',
              borderRadius: '4px',
              cursor: cleanupState === 'loading' ? 'not-allowed' : 'pointer',
              fontSize: '10px',
              letterSpacing: '1px',
              fontFamily: "'Orbitron', sans-serif",
            }}
          >
            <AlertCircle size={13} />
            {cleanupState === 'loading' ? 'LIMPIANDO...' : 'LIMPIAR PUNTOS INVÁLIDOS'}
          </button>
          {cleanupState && cleanupState !== 'loading' && (
            <span style={{ fontSize: '11px', color: '#00b894' }}>{cleanupState}</span>
          )}
        </div>
      </div>

      {/* Overpass / OSM Block */}
      <div style={{ marginBottom: '40px' }}>
        <h2
          style={{
            fontSize: '11px',
            letterSpacing: '2px',
            color: '#636e72',
            marginBottom: '16px',
            borderBottom: '1px solid #1a1f2e',
            paddingBottom: '8px',
          }}
        >
          BÚSQUEDA OPENSTREETMAP (OVERPASS)
        </h2>

        {(() => {
          const osmStat = getStatForSource('osm_turbo');
          return (
            <div
              style={{
                backgroundColor: '#0A0F1A',
                border: '1px solid #1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '680px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <Globe size={18} color="#E27B58" />
                <span style={{ fontSize: '13px', fontWeight: 'bold', letterSpacing: '1px' }}>
                  OpenStreetMap
                </span>
                {osmStat.count > 0 && (
                  <span
                    style={{
                      fontSize: '9px',
                      letterSpacing: '1px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(0,184,148,0.15)',
                      color: '#00b894',
                      border: '1px solid #00b894',
                    }}
                  >
                    {osmStat.count.toLocaleString()} LOCALES
                  </span>
                )}
              </div>
              <p style={{ margin: '0 0 16px', fontSize: '11px', color: '#636e72' }}>
                Busca cualquier marca en OpenStreetMap por nombre y la importa a tu mapa.
              </p>

              <form onSubmit={handleOsmImport} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Nombre de marca (ej: BK)"
                    value={osmBrand}
                    onChange={(e) => setOsmBrand(e.target.value)}
                    required
                    style={{
                      flex: 2,
                      minWidth: '180px',
                      padding: '8px 12px',
                      backgroundColor: '#05070A',
                      border: '1px solid #2d3436',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '12px',
                      outline: 'none',
                      fontFamily: 'sans-serif',
                    }}
                  />
                  <select
                    value={osmCountry}
                    onChange={(e) => setOsmCountry(e.target.value)}
                    style={{
                      flex: 1,
                      minWidth: '110px',
                      padding: '8px 12px',
                      backgroundColor: '#05070A',
                      border: '1px solid #2d3436',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '12px',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="Spain">España</option>
                    <option value="Maroc">Marruecos</option>
                    <option value="France">Francia</option>
                    <option value="Portugal">Portugal</option>
                  </select>
                  <select
                    value={osmType}
                    onChange={(e) => setOsmType(e.target.value)}
                    style={{
                      flex: 1,
                      minWidth: '110px',
                      padding: '8px 12px',
                      backgroundColor: '#05070A',
                      border: '1px solid #2d3436',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '12px',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="competence">Competencia</option>
                    <option value="engine">Motor (Locomotora)</option>
                    <option value="base">Base</option>
                  </select>
                </div>

                {osmResult && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11px',
                      color: osmResult.status === 'success' ? '#00b894' : '#E53935',
                    }}
                  >
                    {osmResult.status === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {osmResult.message}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="submit"
                    disabled={osmLoading || !osmBrand.trim()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '9px 18px',
                      backgroundColor: osmLoading ? '#1a1f2e' : '#0B3D91',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: osmLoading || !osmBrand.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      letterSpacing: '1px',
                      fontFamily: "'Orbitron', sans-serif",
                    }}
                  >
                    {osmLoading ? (
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Search size={14} />
                    )}
                    {osmLoading ? 'BUSCANDO...' : 'BUSCAR E IMPORTAR'}
                  </button>
                  {osmStat.count > 0 && (
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '9px 14px',
                        backgroundColor: 'transparent',
                        color: '#00CEC9',
                        border: '1px solid #00CEC9',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        letterSpacing: '1px',
                        fontFamily: "'Orbitron', sans-serif",
                      }}
                    >
                      <Map size={14} />
                      VER EN MAPA
                    </button>
                  )}
                </div>
              </form>
            </div>
          );
        })()}
      </div>

      {/* Direct API Sources */}
      <div style={{ marginBottom: '40px' }}>
        <h2
          style={{
            fontSize: '11px',
            letterSpacing: '2px',
            color: '#636e72',
            marginBottom: '16px',
            borderBottom: '1px solid #1a1f2e',
            paddingBottom: '8px',
          }}
        >
          FUENTES DIRECTAS
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: '16px' }}>
          {sources.map((src) => {
            const stat = getStatForSource(src.sourceId);
            const state = fetchState[src.key] || { status: 'idle' };
            const isLoading = state.status === 'loading';

            return (
              <div
                key={src.key}
                style={{
                  backgroundColor: '#0A0F1A',
                  border: `1px solid ${state.status === 'success' ? '#00b894' : state.status === 'error' ? '#E53935' : '#1a1f2e'}`,
                  borderRadius: '8px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {/* Title row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: src.color,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '1px' }}>
                      {src.label}
                    </span>
                    {src.origin && (
                      <span
                        title={
                          src.origin === 'osm'
                            ? 'Datos de OpenStreetMap (sin fuente oficial máquina-legible)'
                            : 'Datos de la web/API oficial de la marca'
                        }
                        style={{
                          fontSize: '8px',
                          letterSpacing: '1px',
                          padding: '1px 6px',
                          borderRadius: '10px',
                          border: `1px solid ${src.origin === 'osm' ? '#E27B58' : '#00CEC9'}`,
                          color: src.origin === 'osm' ? '#E27B58' : '#00CEC9',
                        }}
                      >
                        {src.origin === 'osm' ? 'OSM' : 'OFICIAL'}
                      </span>
                    )}
                  </div>
                  {/* Status badge */}
                  <span
                    style={{
                      fontSize: '9px',
                      letterSpacing: '1px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor:
                        stat.status === 'loaded' ? 'rgba(0,184,148,0.15)' : 'rgba(99,110,114,0.2)',
                      color: stat.status === 'loaded' ? '#00b894' : '#636e72',
                      border: `1px solid ${stat.status === 'loaded' ? '#00b894' : '#2d3436'}`,
                    }}
                  >
                    {stat.status === 'loaded' ? 'CARGADO' : 'VACÍO'}
                  </span>
                </div>

                <p style={{ margin: 0, fontSize: '11px', color: '#636e72' }}>{src.description}</p>

                {/* Type selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '9px', color: '#636e72', letterSpacing: '1px', flexShrink: 0 }}>IMPORTAR COMO</span>
                  <select
                    value={sourceTypes[src.key]}
                    onChange={(e) => setSourceTypes((prev) => ({ ...prev, [src.key]: e.target.value }))}
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      backgroundColor: '#05070A',
                      border: '1px solid #2d3436',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontFamily: "'Orbitron', sans-serif",
                    }}
                  >
                    <option value="competence">Competencia</option>
                    <option value="engine">Motor (Locomotora)</option>
                    <option value="base">Base</option>
                  </select>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div>
                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: src.color }}>
                      {stat.count.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '9px', color: '#636e72', letterSpacing: '1px' }}>LOCALES EN DB</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'white' }}>{formatDate(stat.lastIngested)}</div>
                    <div style={{ fontSize: '9px', color: '#636e72', letterSpacing: '1px' }}>ÚLTIMA DESCARGA</div>
                  </div>
                </div>

                {/* Feedback message */}
                {(state.status === 'success' || state.status === 'error') && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11px',
                      color: state.status === 'success' ? '#00b894' : '#E53935',
                    }}
                  >
                    {state.status === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {state.message}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button
                    onClick={() => handleFetch(src)}
                    disabled={isLoading}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      backgroundColor: isLoading ? '#1a1f2e' : '#0B3D91',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      letterSpacing: '1px',
                      fontFamily: "'Orbitron', sans-serif",
                      transition: 'background 0.2s',
                    }}
                  >
                    {isLoading ? (
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Download size={14} />
                    )}
                    {isLoading ? 'DESCARGANDO...' : 'DOWNLOAD'}
                  </button>
                  {stat.count > 0 && (
                    <button
                      onClick={() => navigate('/dashboard')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        backgroundColor: 'transparent',
                        color: '#00CEC9',
                        border: '1px solid #00CEC9',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        letterSpacing: '1px',
                        fontFamily: "'Orbitron', sans-serif",
                      }}
                    >
                      <Map size={14} />
                      VER
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
